/**
 * The compact sticky summary at the top of the detail pane: one status line
 * plus six tiles. It stays put while the variance table scrolls under it, so
 * the totals are on screen the whole time the reviewer works the rows.
 *
 * Colour is load-bearing here and each hue keeps its one meaning (DESIGN.md
 * §1): emerald for stock going up, the sanctioned destructive-secondary orange
 * for stock going down, brand purple for the net, amber for "needs you".
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { formatQty, formatSigned } from '../lib/format';

type TileTone = 'neutral' | 'up' | 'down' | 'net' | 'flag';

const TILE_TONE: Record<TileTone, string> = {
  neutral: 'bg-muted ring-border',
  up: 'bg-emerald-100 text-emerald-800 ring-emerald-300/80',
  down: 'bg-orange-50 text-orange-700 ring-orange-300/80',
  net: 'bg-threadheads-purple/10 text-threadheads-purple ring-threadheads-purple/30',
  flag: 'bg-amber-100 text-amber-800 ring-amber-300/80',
};

function Tile({
  label,
  value,
  tone = 'neutral',
  muted = false,
}: {
  label: string;
  value: string;
  tone?: TileTone;
  muted?: boolean;
}) {
  return (
    // Fixed width, not 1fr: stretching six tiles across a wide monitor turns
    // them into banners and the figures drift apart from their labels (owner
    // feedback 2026-08-05). w-32 fits the widest real value (+999,999) with
    // the label; the row is left-aligned and wraps on narrow panes.
    <div className={cn('w-32 shrink-0 rounded-md px-2.5 py-1.5 ring-1', TILE_TONE[tone])}>
      {/* No opacity dial-down: the tinted tones already sit close to their
          backgrounds, and 80% of an amber-800 on amber-100 drops under AA. */}
      <span className="block truncate text-[9.5px] font-semibold uppercase tracking-wide">
        {label}
      </span>
      <b
        className={cn(
          'block text-[15px] font-semibold leading-tight tabular-nums',
          muted && 'text-muted-foreground',
        )}
      >
        {value}
      </b>
    </div>
  );
}

export interface StocktakeSummaryCounts {
  skuCount: number;
  notCountedCount: number;
  /** UNITS gained across lines counted above expected. */
  increaseUnits: number;
  /** UNITS lost across lines counted below expected (positive number). */
  decreaseUnits: number;
  netChange: number;
  flagsCount: number;
}

export function StocktakeSummaryTiles({ counts }: { counts: StocktakeSummaryCounts }) {
  const { t } = useTranslation('inventory');
  // "Counted" is the number of lines that came back with a figure, not the
  // size of the sheet: `skuCount` includes the blanks, and Counted + Not
  // counted have to add up to it or the row lies.
  const countedLines = counts.skuCount - counts.notCountedCount;
  return (
    <div className="flex flex-wrap gap-1.5">
      <Tile label={t('stocktakes.tile.counted')} value={formatQty(countedLines)} />
      <Tile
        label={t('stocktakes.tile.notCounted')}
        value={formatQty(counts.notCountedCount)}
        muted
      />
      {/* UNITS, signed, so the three movement tiles read as one sentence:
          +1,204 gained, -880 lost, net +324. */}
      <Tile
        label={t('stocktakes.tile.increase')}
        value={formatSigned(counts.increaseUnits)}
        tone="up"
      />
      <Tile
        label={t('stocktakes.tile.decrease')}
        value={formatSigned(-counts.decreaseUnits)}
        tone="down"
      />
      <Tile label={t('stocktakes.tile.net')} value={formatSigned(counts.netChange)} tone="net" />
      <Tile
        label={t('stocktakes.tile.flagged')}
        value={formatQty(counts.flagsCount)}
        tone="flag"
      />
    </div>
  );
}

const FLAGLINE_TONE = {
  warn: 'bg-amber-100 text-amber-800 ring-amber-300/80',
  ok: 'bg-emerald-100 text-emerald-800 ring-emerald-300/80',
  wait: 'bg-threadheads-purple/10 text-threadheads-purple ring-threadheads-purple/30',
} as const;

export function StocktakeFlagline({
  tone,
  icon,
  children,
}: {
  tone: keyof typeof FLAGLINE_TONE;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        'mb-2 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[11.5px] ring-1',
        FLAGLINE_TONE[tone],
      )}
    >
      <span aria-hidden="true" className="shrink-0">
        {icon}
      </span>
      <span className="min-w-0">{children}</span>
    </div>
  );
}
